import React, { useEffect, useMemo, memo } from 'react';
import moment from 'moment';
import './ESMReportNew.scss';
import logo from '../../assets/img/logosManeje/manejebem-logo.svg';

// Componente SVG para Circular Progress - Memoizado para performance
const CircularProgress = memo(({ percentage, color, size = 80 }) => {
  const radius = (size - 10) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      {/* Background circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="#e0e0e0"
        strokeWidth="8"
      />
      {/* Progress circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth="8"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.3s ease' }}
      />
    </svg>
  );
});

CircularProgress.displayName = 'CircularProgress';

// Componente SVG para Gauge Chart - Memoizado para performance
const GaugeChart = memo(({ value, size = 240 }) => {
  // Calcular o ângulo correto do ponteiro baseado nos segmentos
  // Gauge vai de -90° a +90° (total 180°)
  // 0% = -90°, 20% = -54°, 40% = -18°, 60% = +18°, 80% = +54°, 100% = +90°
  // Para 68.7% está entre 60% (+18°) e 80% (+54°)
  // Interpolação: 18 + ((68.7-60)/(80-60)) * (54-18) = 18 + (8.7/20) * 36 = 18 + 15.66 = 33.66°
  const angle = -90 + (value / 100) * 180;
  const centerX = 120;
  const centerY = 120;
  const radius = 90;
  const strokeWidth = 20;
  
  // Função auxiliar para calcular o caminho do arco
  const describeArc = (x, y, radius, startAngle, endAngle) => {
    const start = polarToCartesian(x, y, radius, endAngle);
    const end = polarToCartesian(x, y, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
    return [
      "M", start.x, start.y, 
      "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y
    ].join(" ");
  };
  
  const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
    return {
      x: centerX + (radius * Math.cos(angleInRadians)),
      y: centerY + (radius * Math.sin(angleInRadians))
    };
  };
  
  return (
    <svg width={size} height={size / 2 + 40} viewBox="0 0 240 160">
      {/* Segmento vermelho (0-20%) */}
      <path
        d={describeArc(centerX, centerY, radius, -90, -54)}
        fill="none"
        stroke="#ff0002"
        strokeWidth={strokeWidth}
      />
      {/* Segmento laranja (20-40%) */}
      <path
        d={describeArc(centerX, centerY, radius, -54, -18)}
        fill="none"
        stroke="#ff8c02"
        strokeWidth={strokeWidth}
      />
      {/* Segmento amarelo (40-60%) */}
      <path
        d={describeArc(centerX, centerY, radius, -18, 18)}
        fill="none"
        stroke="#ffcc01"
        strokeWidth={strokeWidth}
      />
      {/* Segmento verde claro (60-80%) */}
      <path
        d={describeArc(centerX, centerY, radius, 18, 54)}
        fill="none"
        stroke="#41a67c"
        strokeWidth={strokeWidth}
      />
      {/* Segmento verde escuro (80-100%) */}
      <path
        d={describeArc(centerX, centerY, radius, 54, 90)}
        fill="none"
        stroke="#0b7348"
        strokeWidth={strokeWidth}
      />
      
      {/* Pointer */}
      <g transform={`translate(${centerX}, ${centerY}) rotate(${angle})`}>
        <line
          x1="0"
          y1="0"
          x2="0"
          y2="-70"
          stroke="#000"
          strokeWidth="3"
        />
        <circle cx="0" cy="0" r="8" fill="#000" />
      </g>
      
      {/* Center white cover */}
      <circle cx={centerX} cy={centerY} r="15" fill="white" />
    </svg>
  );
});

GaugeChart.displayName = 'GaugeChart';

const ESMReportNew = memo(({ esm = {}, esmData = {}, barometerData = [], categories = [], data = [], forPrint = false }) => {
  // Force styles to be applied when printing
  useEffect(() => {
    if (forPrint) {
      // Add a class to the body when this component is rendered for printing
      document.body.classList.add('printing-esm-report');
      return () => {
        document.body.classList.remove('printing-esm-report');
      };
    }
  }, [forPrint]);

  // Função para formatar data
  const formatDate = (date) => {
    if (!date) return moment().format('DD/MM/YYYY');
    return moment(date).format('DD/MM/YYYY');
  };

  // Função para obter dados do primeiro item do barometerData
  const getBarometerItem = () => {
    return barometerData.length > 0 ? barometerData[0] : {};
  };

  // Função para obter dados do usuário - adaptada para ESM Comunidade
  const getUserData = () => {
    const barometer = getBarometerItem();
    
    // Tentar obter dados do barometer primeiro
    if (barometer.user && barometer.user.length > 0) {
      return barometer.user[0];
    }
    
    // Se não encontrar no barometer, tentar no esm direto
    if (esm && esm.user && esm.user.length > 0) {
      return esm.user[0];
    }
    
    // Fallback para ESM Comunidade
    return {
      name: 'ESM Comunidade',
      email: ''
    };
  };

  // Função para processar campos - usar os dados da tela principal
  const getFields = () => {
    // Primeiro tentar usar os dados passados pela tela principal
    if (data && data.length > 0) {
      return data;
    }
    
    // Fallback para barometer
    const barometer = getBarometerItem();
    if (barometer.fields && barometer.fields.length > 0) {
      return barometer.fields;
    }
    
    // Se não encontrar no barometer, tentar no esm direto
    if (esm && esm.fields && esm.fields.length > 0) {
      return esm.fields;
    }
    
    // Retornar array vazio se não encontrar dados
    return [];
  };

  // Função para obter resumo por categoria - adaptada para ESM Comunidade
  const getSummary = () => {
    const barometer = getBarometerItem();
    
    // Tentar obter summary do barometer primeiro
    if (barometer.summary && Object.keys(barometer.summary).length > 0) {
      return barometer.summary;
    }
    
    // Se não encontrar no barometer, tentar no esm direto
    if (esm && esm.summary && Object.keys(esm.summary).length > 0) {
      return esm.summary;
    }
    
    // Fallback com valores padrão
    return {
      Economico: 0,
      Agronomico: 0,
      Social: 0,
      Ambiental: 0
    };
  };
  
  // Função para obter descrição do desempenho
  const getPerformanceText = (score) => {
    const value = safeNumber(score, 0);
    if (value >= 80) return 'Excelente desempenho';
    if (value >= 60) return 'Bom desempenho';
    if (value >= 40) return 'Desempenho médio';
    if (value >= 20) return 'Baixo desempenho';
    return 'Desempenho ruim';
  };

  // Função para obter detalhes - adaptada para ESM Comunidade
  const getDetails = () => {
    const barometer = getBarometerItem();
    
    // Tentar obter details do barometer primeiro
    if (barometer.details && Object.keys(barometer.details).length > 0) {
      return barometer.details;
    }
    
    // Se não encontrar no barometer, tentar no esm direto
    if (esm && esm.details && Object.keys(esm.details).length > 0) {
      return esm.details;
    }
    
    // Fallback com valor padrão
    return {
      Geral: 0
    };
  };

  // Função para obter classe baseada no conceito
  const getConceptClass = (concept) => {
    if (!concept) return 'ruim';
    
    const conceptLower = String(concept).toLowerCase().trim();
    
    // Mapeamento de conceitos para classes CSS
    if (conceptLower === 'alto' || conceptLower === 'sustentável') return 'alto';
    if (conceptLower === 'bom' || conceptLower === 'quase sustentável') return 'bom';
    if (conceptLower === 'médio' || conceptLower === 'medio' || conceptLower === 'intermediário') return 'medio';
    if (conceptLower === 'baixo' || conceptLower === 'quase insustentável') return 'baixo';
    if (conceptLower === 'ruim' || conceptLower === 'insustentável') return 'ruim';
    
    // Fallback baseado em valores numéricos
    const numValue = parseFloat(concept);
    if (!isNaN(numValue)) {
      if (numValue >= 80) return 'alto';
      if (numValue >= 60) return 'bom';
      if (numValue >= 40) return 'medio';
      if (numValue >= 20) return 'baixo';
      return 'ruim';
    }
    
    return 'ruim';
  };
  
  // Função para formatar número com proteção contra NaN
  const formatNumber = (value) => {
    if (value === undefined || value === null || isNaN(Number(value))) return '0.0';
    return Number(value).toFixed(1);
  };
  
  // Função para garantir que valores numéricos são válidos
  const safeNumber = (value, defaultValue = 0) => {
    const num = parseFloat(value);
    return isNaN(num) ? defaultValue : num;
  };
  
  // Função para obter cor baseada no valor - cores corretas do ESM Producer
  const getScoreColor = (value) => {
    const score = safeNumber(value, 0);
    if (score >= 80) return '#0b7348'; // Alto (verde escuro)
    if (score >= 60) return '#41a67c'; // Bom (verde claro)
    if (score >= 40) return '#ffcc01'; // Médio (amarelo)
    if (score >= 20) return '#ff8c02'; // Baixo (laranja)
    return '#ff0002';                   // Ruim (vermelho)
  };

  // Usar useMemo para evitar recálculos desnecessários
  const userData = useMemo(() => getUserData(), [barometerData, esm]);
  const fields = useMemo(() => getFields(), [data, barometerData, esm]);
  const summary = useMemo(() => getSummary(), [barometerData, esm]);
  const details = useMemo(() => getDetails(), [barometerData, esm]);
  
  // Usar a mesma lógica da tela principal para o score geral
  const scoreGeral = useMemo(() => {
    const esmGeral = esm?.details?.["Geral"];
    if (esmGeral) return safeNumber(esmGeral, 0);
    
    const categoryGeral = categories.find(({ category }) => category === "GeneralMean")?.mean;
    return safeNumber(categoryGeral, 0);
  }, [esm, categories]);
  
  // Usar dados das categories para os scores das dimensões
  const economicoScore = useMemo(() => {
    const esmEconomico = esm?.summary?.["Economico"] || esm?.summary?.["Econômico"];
    if (esmEconomico) return safeNumber(esmEconomico, 0);
    
    const categoryEconomico = categories.find(({ category }) => category === "Econômico" || category === "Economico")?.mean;
    return safeNumber(categoryEconomico, 0);
  }, [esm, categories]);
  
  const agronomicScore = useMemo(() => {
    const esmAgronomico = esm?.summary?.["Agronomico"] || esm?.summary?.["Agronômico"];
    if (esmAgronomico) return safeNumber(esmAgronomico, 0);
    
    const categoryAgronomico = categories.find(({ category }) => category === "Agronômico" || category === "Agronomico")?.mean;
    return safeNumber(categoryAgronomico, 0);
  }, [esm, categories]);
  
  const socialScore = useMemo(() => {
    const esmSocial = esm?.summary?.["Social"];
    if (esmSocial) return safeNumber(esmSocial, 0);
    
    const categorySocial = categories.find(({ category }) => category === "Social")?.mean;
    return safeNumber(categorySocial, 0);
  }, [esm, categories]);
  
  const ambientalScore = useMemo(() => {
    const esmAmbiental = esm?.summary?.["Ambiental"];
    if (esmAmbiental) return safeNumber(esmAmbiental, 0);
    
    const categoryAmbiental = categories.find(({ category }) => category === "Ambiental")?.mean;
    return safeNumber(categoryAmbiental, 0);
  }, [esm, categories]);

  return (
    <div className="esm-report-new">
      <div className="report-page cover-page" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* Nome do produtor */}
        <div style={{ 
          backgroundColor: '#f5f5f5', 
          padding: '20px', 
          borderRadius: '8px', 
          width: '100%',
          marginBottom: '30px',
          textAlign: 'center'
        }}>
          <h2 style={{ margin: '0 0 10px 0', fontSize: '24px' }}>{userData.name || 'Esm Comunidade'}</h2>
          <div className="user-email" style={{ color: '#666', fontSize: '16px' }}>{userData.email || ''}</div>
        </div>
        
        {/* Nota principal centralizada */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <h1 style={{ fontSize: '36px', marginBottom: '20px' }}>Nota: {formatNumber(scoreGeral)}</h1>
          {/* Gauge chart */}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <GaugeChart value={scoreGeral} size={200} />
          </div>
        </div>
        
        <div className="nivel-section">
          <div className="nivel-title" style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>Nível</div>
          <div className="nivel-indicators" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '10px' }}>
            <div className="nivel-indicator" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <div className="color-dot alto" style={{ 
                width: '15px', 
                height: '15px', 
                borderRadius: '50%', 
                backgroundColor: '#0b7348',
                display: 'inline-block' 
              }}></div>
              <div className="indicator-text">Alto (sustentável)</div>
            </div>
            <div className="nivel-indicator" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <div className="color-dot bom" style={{ 
                width: '15px', 
                height: '15px', 
                borderRadius: '50%', 
                backgroundColor: '#41a67c',
                display: 'inline-block' 
              }}></div>
              <div className="indicator-text">Bom (Quase sustentável)</div>
            </div>
            <div className="nivel-indicator" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <div className="color-dot medio" style={{ 
                width: '15px', 
                height: '15px', 
                borderRadius: '50%', 
                backgroundColor: '#ffcc01',
                display: 'inline-block' 
              }}></div>
              <div className="indicator-text">Médio (Intermediário)</div>
            </div>
            <div className="nivel-indicator" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <div className="color-dot baixo" style={{ 
                width: '15px', 
                height: '15px', 
                borderRadius: '50%', 
                backgroundColor: '#ff8c02',
                display: 'inline-block' 
              }}></div>
              <div className="indicator-text">Baixo (Quase insustentável)</div>
            </div>
            <div className="nivel-indicator" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <div className="color-dot ruim" style={{ 
                width: '15px', 
                height: '15px', 
                borderRadius: '50%', 
                backgroundColor: '#ff0002',
                display: 'inline-block' 
              }}></div>
              <div className="indicator-text">Ruim (Insustentável)</div>
            </div>
          </div>
          
          <div className="nivel-scores" style={{ 
            display: 'flex', 
            justifyContent: 'space-around', 
            margin: '30px 0',
            width: '100%',
            flexWrap: 'wrap',
            gap: '20px'
          }}>
            {/* Ambiental */}
            <div style={{ textAlign: 'center', width: '22%' }}>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <CircularProgress percentage={ambientalScore} color={getScoreColor(ambientalScore)} size={80} />
                <div style={{ 
                  position: 'absolute', 
                  top: '50%', 
                  left: '50%', 
                  transform: 'translate(-50%, -50%)',
                  fontSize: '20px',
                  fontWeight: 'bold'
                }}>
                  {formatNumber(ambientalScore)}
                </div>
              </div>
              <div className="score-label" style={{ marginTop: '10px', fontWeight: 'bold' }}>Ambiental</div>
              <div style={{ fontSize: '12px', color: '#666' }}>{getPerformanceText(ambientalScore)}</div>
            </div>
            
            {/* Agronômico */}
            <div style={{ textAlign: 'center', width: '22%' }}>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <CircularProgress percentage={agronomicScore} color={getScoreColor(agronomicScore)} size={80} />
                <div style={{ 
                  position: 'absolute', 
                  top: '50%', 
                  left: '50%', 
                  transform: 'translate(-50%, -50%)',
                  fontSize: '20px',
                  fontWeight: 'bold'
                }}>
                  {formatNumber(agronomicScore)}
                </div>
              </div>
              <div className="score-label" style={{ marginTop: '10px', fontWeight: 'bold' }}>Agronômico</div>
              <div style={{ fontSize: '12px', color: '#666' }}>{getPerformanceText(agronomicScore)}</div>
            </div>
            
            {/* Social */}
            <div style={{ textAlign: 'center', width: '22%' }}>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <CircularProgress percentage={socialScore} color={getScoreColor(socialScore)} size={80} />
                <div style={{ 
                  position: 'absolute', 
                  top: '50%', 
                  left: '50%', 
                  transform: 'translate(-50%, -50%)',
                  fontSize: '20px',
                  fontWeight: 'bold'
                }}>
                  {formatNumber(socialScore)}
                </div>
              </div>
              <div className="score-label" style={{ marginTop: '10px', fontWeight: 'bold' }}>Social</div>
              <div style={{ fontSize: '12px', color: '#666' }}>{getPerformanceText(socialScore)}</div>
            </div>
            
            {/* Econômico */}
            <div style={{ textAlign: 'center', width: '22%' }}>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <CircularProgress percentage={economicoScore} color={getScoreColor(economicoScore)} size={80} />
                <div style={{ 
                  position: 'absolute', 
                  top: '50%', 
                  left: '50%', 
                  transform: 'translate(-50%, -50%)',
                  fontSize: '20px',
                  fontWeight: 'bold'
                }}>
                  {formatNumber(economicoScore)}
                </div>
              </div>
              <div className="score-label" style={{ marginTop: '10px', fontWeight: 'bold' }}>Econômico</div>
              <div style={{ fontSize: '12px', color: '#666' }}>{getPerformanceText(economicoScore)}</div>
            </div>
          </div>
        </div>

        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse', 
          marginTop: '20px',
          border: '1px solid #ddd'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#f2f2f2' }}>
              <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Nome</th>
              <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Etapa</th>
              <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Conceito</th>
              <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Índice</th>
            </tr>
          </thead>
          <tbody>
            {fields.length === 0 ? (
              <tr>
                <td colSpan="4" style={{ padding: '20px', textAlign: 'center', fontStyle: 'italic', color: '#666' }}>
                  Nenhum dado encontrado para exibir na tabela
                </td>
              </tr>
            ) : (
            <>
              {fields.map((field, index) => {
              // Garantir que o valor seja um número válido - adaptado para ESM Comunidade
              const fieldValue = field?.value && !isNaN(Number(field?.value)) ? field.value : 0;
              
              // Para ESM Comunidade, concept pode estar em 'concept' ao invés de 'conceito'
              const fieldConcept = field.conceito || field.concept || 'Ruim';
              
              // Nome do campo - adaptado para diferentes estruturas
              const fieldName = field.name || field.title || field.indicatorName || `Campo ${index + 1}`;
              
              // Categoria - pode vir de diferentes propriedades
              const fieldCategory = field.category || field.scaleCategory || field.dimension || 'Geral';
              
              return (
                <tr key={field.id || field.scaleIndicatorId || index} style={{ 
                  backgroundColor: index % 2 === 0 ? '#fff' : '#f9f9f9',
                  borderBottom: '1px solid #ddd'
                }}>
                  <td style={{ padding: '8px', textAlign: 'left' }} className="field-name">
                    {fieldName}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'left' }}>
                    {fieldCategory}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center' }}>
                    <span 
                      className={`conceito ${getConceptClass(fieldConcept)}`} 
                      style={{ 
                        backgroundColor: getScoreColor(fieldValue),
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontWeight: 'bold',
                        display: 'inline-block',
                        width: '80%',
                        textAlign: 'center'
                      }}
                    >
                      {fieldConcept}
                    </span>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', fontWeight: 'bold' }} className="field-index">
                    {formatNumber(fieldValue)}
                  </td>
                </tr>
              );
            })}
            </>
            )}
          </tbody>
        </table>
        
        <div className="report-footer" style={{ marginTop: '30px' }}>
          <img src={logo} alt="ManejeBem" className="footer" style={{ height: '40px' }} />
        </div>
      </div>
    </div>
  );
});

ESMReportNew.displayName = 'ESMReportNew';

export default ESMReportNew;
